import { useState } from 'react';
import { useFormik } from 'formik';

import useToast from '../../../hooks/useToast';
import { workReportLineStatus } from '../../../utils/dataStatus';
import {
  approveAdapter,
  approveAllAdapter,
  approveLocationProgramPointApdater,
  editLocationProgramPointAdapter,
  editQuantityAdapter,
  getLocationProgramPointAdapter,
  getPerfilEjecuta,
  obtainOperativeData,
  postLocationProgramPointAdapter,
  rectifyLocationProgramPointAdapter,
  rejectLocationProgramPointAdapter,
  rejectLocationWithoutProgramPointAdapter
} from '../../../services/programPointsService';

const locationsInit = [
  {
    pozoId: 1,
    pozoNombre: null,
    nombreEquipo: null,
    eventoSigla: null,
    habilitaVentana: false,
    totalValue: 0,
    totalValueFijado: 0,
    cantidad: 0,
    programPoints: [
      {
        quantity: null,
        points: null,
        quantityFijados: null,
        pointsFijados: null,
        idPartePuntoPrograma: null,
        idParteDiario: null,
        cost: null,
        code: null,
        subCode: null,
        detail: null,
        clasification: null,
        idOperativo: null
      }
    ]
  }
];

export const defaultInitialValues = {
  locations: locationsInit,
  locationsCopy: locationsInit,
  locationTotalValue: 0,
  locationTotalValueFijado: 0
};

const useLocationProgramPoints = (
  { line, initialValues = defaultInitialValues, validationSchema } = {
    line: undefined,
    initialValues: defaultInitialValues,
    validationSchema: undefined
  }
) => {
  const { setNewToast } = useToast();
  const [loading, setLoading] = useState(false);

  const { values, setFieldValue, resetForm, setFieldTouched, touched, errors } = useFormik({
    initialValues,
    validationSchema
  });

  const calculateTotalValue = (locations) => {
    const locationTotalValue = parseFloat(
      locations.reduce((acc, loc) => Number(acc) + Number(loc.totalValue), 0).toFixed(3)
    );
    const locationTotalValueFijado = parseFloat(
      locations.reduce((acc, loc) => Number(acc) + Number(loc.totalValueFijado), 0).toFixed(3)
    );

    return {
      locationTotalValue,
      locationTotalValueFijado
    };
  };

  const calcularTotales = (locations, fijar = false) => {
    const locationsAdapted = locations.map((location) => {
      const totalValue = location.programPoints.reduce(
        (acc, curr) => acc + Number(curr.quantity),
        0
      );
      const totalValueFijado = fijar
        ? location.programPoints.reduce((acc, curr) => acc + Number(curr.quantityFijados), 0)
        : totalValue;

      return {
        ...location,
        totalValue,
        totalValueFijado: totalValueFijado || totalValue
      };
    });

    const { locationTotalValue, locationTotalValueFijado } = calculateTotalValue(locationsAdapted);

    return {
      locations: locationsAdapted,
      locationTotalValue,
      locationTotalValueFijado
    };
  };

  const getLocationWithOperativeData = (locations, dataWithOD) =>
    locations.map((location) => ({
      ...location,
      programPoints: location?.programPoints.map((pp) => {
        const ppWithOperativeData = {
          ...pp,
          clasification: pp?.clasification || dataWithOD?.clasification || null,
          cost: dataWithOD?.costoClase?.[0] || null,
          detail: dataWithOD?.descripcion || '',
          code: dataWithOD?.codigo?.[0] || '',
          subCode: dataWithOD?.subcodigo?.[0] || '',
          points: pp.points || dataWithOD?.puntoPrograma || null
        };

        return ppWithOperativeData;
      })
    }));

  const getFirstNonEmptyString = (...valuesToEvaluate) => {
    const found = valuesToEvaluate.find(
      (value) => typeof value === 'string' && value.trim().length > 0
    );

    return found || '';
  };

  const isObjectWithValues = (value) =>
    Boolean(value && typeof value === 'object' && !Array.isArray(value));

  const normalizeSimpleSelectValue = (value) => {
    if (!value) return null;

    if (typeof value === 'string') {
      if (!value.trim()) return null;

      return {
        nombre: value,
        id: value,
        value,
        label: value
      };
    }

    if (!isObjectWithValues(value)) return null;

    const normalizedValue = getFirstNonEmptyString(
      value?.value,
      value?.id,
      value?.nombre,
      value?.label
    );

    if (!normalizedValue) return null;

    return {
      ...value,
      nombre: getFirstNonEmptyString(value?.nombre, value?.label, normalizedValue),
      id: getFirstNonEmptyString(value?.id, value?.value, normalizedValue),
      value: getFirstNonEmptyString(value?.value, value?.id, normalizedValue),
      label: getFirstNonEmptyString(value?.label, value?.nombre, normalizedValue)
    };
  };

  const normalizeCostSelectValue = (value) => {
    if (!isObjectWithValues(value)) return null;

    const costoClase = getFirstNonEmptyString(value?.costoClase, value?.nombre, value?.nombreCorto);
    const fase = getFirstNonEmptyString(value?.fase);
    const combinacion = getFirstNonEmptyString(
      value?.combinacion,
      value?.nombreLargo,
      value?.label,
      value?.nombre
    );
    const id = getFirstNonEmptyString(value?.id, value?.value, `${costoClase} | ${fase}`);
    const nombre = getFirstNonEmptyString(value?.nombre, combinacion, costoClase);
    const label = getFirstNonEmptyString(
      value?.label,
      costoClase && fase ? `${costoClase} - ${fase}` : '',
      combinacion,
      nombre
    );

    if (!nombre && !label && !id) return null;

    return {
      ...value,
      costoClase,
      fase,
      combinacion,
      nombre,
      nombreCorto: getFirstNonEmptyString(value?.nombreCorto, costoClase),
      nombreLargo: getFirstNonEmptyString(value?.nombreLargo, combinacion),
      id,
      value: getFirstNonEmptyString(value?.value, id),
      label
    };
  };

  const costAdapter = (pp, dataWithOD) => {
    const normalizedCurrentCost = normalizeCostSelectValue(pp?.cost);

    if (normalizedCurrentCost) {
      return normalizedCurrentCost;
    }

    if (dataWithOD?.costoClase?.[0]) {
      const clase = dataWithOD?.costoClase?.[0];
      const hasCostData = Boolean(
        getFirstNonEmptyString(clase?.combinacion, clase?.costoClase, clase?.fase)
      );

      if (!hasCostData) return null;

      // temporal change
      const firstWord = clase?.combinacion?.split(' ')[0] || '';

      return normalizeCostSelectValue({
        value: dataWithOD?.costoClase.length > 0 ? clase.combinacion : '',
        label: dataWithOD?.costoClase.length > 0 ? clase.combinacion : '',
        nombre: firstWord,
        costoClase: dataWithOD?.costoClase.length > 0 ? clase.costoClase : '',
        fase: dataWithOD?.costoClase.length > 0 ? clase.fase : '',
        combinacion: dataWithOD?.costoClase.length > 0 ? clase.combinacion : ''
      });
    }

    return null;
  };

  const codeAdapter = (pp, dataWithOD) => {
    const normalizedCurrentCode = normalizeSimpleSelectValue(pp?.code);
    const code = dataWithOD?.codigo?.[0] || '';

    if (normalizedCurrentCode) {
      return normalizedCurrentCode;
    }

    if (code) {
      return normalizeSimpleSelectValue({
        nombre: code,
        id: code,
        value: code,
        label: code
      });
    }

    return null;
  };

  const subCodeAdapter = (pp, dataWithOD) => {
    const normalizedCurrentSubCode = normalizeSimpleSelectValue(pp?.subCode);
    const subCode = dataWithOD?.subcodigo?.[0] || '';

    if (normalizedCurrentSubCode) {
      return normalizedCurrentSubCode;
    }

    if (subCode) {
      return normalizeSimpleSelectValue({
        nombre: subCode,
        id: subCode,
        value: subCode,
        label: subCode
      });
    }

    return null;
  };

  const buildProgramPointWithOperativeData = (pp, dataWithOD) => {
    const costValue = costAdapter(pp, dataWithOD);
    const codeValue = codeAdapter(pp, dataWithOD);
    const subCodeValue = subCodeAdapter(pp, dataWithOD);

    return {
      ...pp,
      cost: costValue,
      detail: pp.detail || dataWithOD?.descripcion || '',
      code: codeValue,
      subCode: subCodeValue,
      points: pp?.points || dataWithOD?.puntoPrograma || null
    };
  };

  const getLocationWithOperativeDataForSelects = (locations, dataWithOD) =>
    locations.map((location) => ({
      ...location,
      programPoints: location?.programPoints.flatMap((pp) => {
        const data = dataWithOD
          .filter((p) => p?.idPozo === location?.pozoId)
          .map((p) => buildProgramPointWithOperativeData(pp, p));

        return data;
      })
    }));

  const obtainProgramPoints = async (body) => {
    try {
      setLoading(true);
      const obtainedProgramPoints = await obtainOperativeData(body);

      setLoading(false);

      return obtainedProgramPoints;
    } catch (error) {
      setLoading(false);
    }

    return null;
  };

  const getProgramPointsData = async ({
    perfilEjecutaParam,
    saveInitialCopy = false,
    isEditModal = false,
    skipObtainProgramPoints = false
  } = {}) => {
    try {
      setLoading(true);

      const esFijado = line.estadoEnum === workReportLineStatus.fijar;

      const perfilEjecuta = perfilEjecutaParam || getPerfilEjecuta();

      const res = await getLocationProgramPointAdapter(line, perfilEjecuta);

      let locationsWithOperativeData = [];
      let newLocationAdapter = [];

      const adapterResLocation = res.flatMap((r) =>
        r?.programPoints?.map((programPoint) => ({
          idParteDiario: programPoint?.idParteDiario,
          idPozo: r.pozoId
        }))
      );

      const uniqueAdapterResLocation = [
        ...new Map(
          adapterResLocation.map((item) => [`${item.idPozo}-${item.idParteDiario}`, item])
        ).values()
      ];

      if (!skipObtainProgramPoints) {
        locationsWithOperativeData = await obtainProgramPoints(uniqueAdapterResLocation);
        newLocationAdapter = getLocationWithOperativeDataForSelects(
          res,
          locationsWithOperativeData
        );
      } else {
        // Cuando es inspector, usar los datos originales sin obtener datos operacionales
        newLocationAdapter = res;
      }

      const { locations, locationTotalValue, locationTotalValueFijado } = calcularTotales(
        newLocationAdapter,
        esFijado
      );

      setFieldValue(`locationTotalValue`, locationTotalValue);
      setFieldValue(`locationTotalValueFijado`, locationTotalValueFijado);
      setFieldValue(`locations`, locations);
      if (saveInitialCopy) {
        setFieldValue(`locationsCopy`, isEditModal ? res : locations);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const postLocationProgramPoints = async (locations) => {
    try {
      setLoading(true);

      const result = await postLocationProgramPointAdapter(locations, line);

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diarios notificados con éxito.'
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const rectifyLocationProgramPoints = async (cantidad, locations, comentario) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();

      const result = await rectifyLocationProgramPointAdapter(
        {
          ...line,
          comentarios: comentario || line?.comentarios || '',
          revisionExperta: line?.revisionExperta ?? null,
          requiereConformidad: line?.requiereConformidad ?? null,
          requiereAdjuntar: line?.requiereAdjuntar ?? null
        },
        cantidad,
        locations,
        perfilEjecuta
      );

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario rectificados con éxito.'
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const approveLocationProgramPoints = async (locations, lineData = line) => {
    try {
      setLoading(true);
      const cantidadTotal = calculateTotalValue(locations);
      const result = await approveLocationProgramPointApdater(
        lineData,
        cantidadTotal?.locationTotalValue || 0,
        locations
      );

      if (result?.statusCode === 400) {
        setNewToast({
          type: 'ERROR',
          message: result.mensaje || result.message
        });
      } else {
        setNewToast({
          type: 'SUCCESS',
          message: result.mensaje || result.message
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const approveAll = async (ids) => {
    try {
      setLoading(true);
      const result = await approveAllAdapter(ids);

      if (result?.statusCode === 400) {
        setNewToast({
          type: 'ERROR',
          message: result.mensaje || result.message
        });
      } else {
        setNewToast({
          type: 'SUCCESS',
          message: result.mensaje || result.message
        });
      }

      return result;
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
      setLoading(false);
    }

    return null;
  };

  const approve = async (idParteDiario) => {
    try {
      setLoading(true);
      const result = await approveAdapter(idParteDiario);

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || result.message
      });
    } catch (error) {
      setLoading(false);
    }
  };

  const rejectWithProgramPoints = async ({
    cantidad,
    comentario,
    requiereConformidad,
    requiereAdjuntar,
    locations
  }) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();
      const result = await rejectLocationProgramPointAdapter(
        line,
        cantidad,
        comentario,
        locations,
        requiereConformidad,
        requiereAdjuntar,
        perfilEjecuta
      );

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario devueltos con éxito.'
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const rejectQuantity = async ({
    cantidad,
    comentario,
    requiereConformidad,
    requiereAdjuntar
  }) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();
      const result = await rejectLocationWithoutProgramPointAdapter(
        line,
        cantidad,
        comentario,
        requiereConformidad,
        requiereAdjuntar,
        perfilEjecuta
      );

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario devueltos con éxito.'
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const editWithProgramPoints = async ({ cantidad, comentario, locations }) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();
      const result = await editLocationProgramPointAdapter(
        line,
        cantidad,
        comentario,
        locations,
        perfilEjecuta
      );

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario editados con éxito.'
      });
      setLoading(false);

      return result;
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }

    return null;
  };

  const editQuantity = async ({ cantidad, comentario }) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();
      const result = await editQuantityAdapter(line, cantidad, comentario, perfilEjecuta);

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario editados con éxito.'
      });
      setLoading(false);

      return result;
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }

    return null;
  };

  return {
    calcularTotales,
    calculateTotalValue,
    getProgramPointsData,
    postLocationProgramPoints,
    rectifyLocationProgramPoints,
    approveLocationProgramPoints,
    rejectWithProgramPoints,
    editWithProgramPoints,
    editQuantity,
    rejectQuantity,
    obtainProgramPoints,
    getLocationWithOperativeData,
    approve,
    approveAll,
    loading,
    setLoading,
    values,
    resetForm,
    setFieldValue,
    setFieldTouched,
    touched,
    errors
  };
};

export default useLocationProgramPoints;
